import { useCallback, useEffect, useState } from 'react';
import { parseAsInteger, parseAsString, useQueryStates } from 'nuqs';
import type {
  LupaRiskSortBy,
  LupaRiskSortDirection,
} from '../../services/api/types.gen';
import { LUPARISKSORTBY_VALUES } from '../../services/api/models/LupaRiskSortBy';
import { formatDateToISO, parseISOToLocalDate } from '../../utils/dateUtils';
import {
  coerceReportPageSize,
  readPositiveIntegerParam,
} from '../../utils/reportDisplay';
import { useDebounce } from '../useDebounce';
import {
  createReportSortParser,
  parseAsReportDate,
  parseAsReportSortDirection,
} from './reportQueryParsers';

const DEFAULT_SORT_BY: LupaRiskSortBy = 'primary_difference';
const DEFAULT_SORT_DIRECTION: LupaRiskSortDirection = 'asc';

const lupaRiskReportParsers = {
  as_of_date: parseAsReportDate,
  clinic_id: parseAsString.withDefault(''),
  q: parseAsString.withDefault(''),
  sort_by: createReportSortParser(LUPARISKSORTBY_VALUES).withDefault(
    DEFAULT_SORT_BY
  ),
  sort_direction: parseAsReportSortDirection.withDefault(
    DEFAULT_SORT_DIRECTION
  ),
  page: parseAsInteger.withDefault(1),
  page_size: parseAsInteger.withDefault(25),
};

export interface LupaRiskReportParams {
  asOfDate: string | null;
  clinicId: string;
  query: string;
  sortBy: LupaRiskSortBy;
  sortDirection: LupaRiskSortDirection;
  page: number;
  pageSize: 25 | 50 | 100;
}

export function useLupaRiskReportParams() {
  const [urlParams, setUrlParams] = useQueryStates(lupaRiskReportParsers, {
    history: 'replace',
    clearOnDefault: true,
  });
  const [searchInput, setSearchInput] = useState(urlParams.q);

  const params: LupaRiskReportParams = {
    asOfDate: urlParams.as_of_date
      ? formatDateToISO(urlParams.as_of_date)
      : null,
    clinicId: urlParams.clinic_id,
    query: urlParams.q,
    sortBy: urlParams.sort_by,
    sortDirection: urlParams.sort_direction,
    page: readPositiveIntegerParam(String(urlParams.page), 1),
    pageSize: coerceReportPageSize(urlParams.page_size),
  };

  const updateParams = useCallback(
    (
      updates: Partial<{
        as_of_date: Date | null;
        clinic_id: string | null;
        q: string | null;
        sort_by: LupaRiskSortBy | null;
        sort_direction: LupaRiskSortDirection | null;
        page: number | null;
        page_size: number | null;
      }>,
      resetPage = true
    ) => {
      void setUrlParams({
        ...updates,
        ...(resetPage ? { page: 1 } : {}),
      });
    },
    [setUrlParams]
  );

  const setSearchQuery = useCallback(
    (value: string) => updateParams({ q: value || null }),
    [updateParams]
  );
  const debouncedSetSearchQuery = useDebounce(setSearchQuery, 300);

  useEffect(() => {
    debouncedSetSearchQuery.cancel();
    setSearchInput(urlParams.q);
  }, [debouncedSetSearchQuery, urlParams.q]);

  const setAsOfDate = useCallback(
    (value: Date | null) => updateParams({ as_of_date: value }),
    [updateParams]
  );
  const persistResolvedAsOfDate = useCallback(
    (value: string) => {
      if (urlParams.as_of_date !== null) return;
      const parsed = parseISOToLocalDate(value);
      if (parsed) updateParams({ as_of_date: parsed }, false);
    },
    [updateParams, urlParams.as_of_date]
  );
  const setClinicId = useCallback(
    (value: string | null) => updateParams({ clinic_id: value }),
    [updateParams]
  );
  const setSorting = useCallback(
    (sortBy: LupaRiskSortBy, sortDirection: LupaRiskSortDirection) =>
      updateParams({ sort_by: sortBy, sort_direction: sortDirection }),
    [updateParams]
  );
  const setPagination = useCallback(
    (page: number, pageSize: number) =>
      updateParams({ page, page_size: pageSize }, false),
    [updateParams]
  );

  return {
    params,
    searchInput,
    setSearchInput,
    setSearchQuery: debouncedSetSearchQuery,
    isSearchPending: searchInput !== params.query,
    setAsOfDate,
    persistResolvedAsOfDate,
    setClinicId,
    setSorting,
    setPagination,
  };
}

export { DEFAULT_SORT_BY, DEFAULT_SORT_DIRECTION };
